import { useRef, useState } from 'react';
import { TimestampService } from '@zlinkx/shared-v2';
import { useTranslation } from '@/i18n';
import { useCardBalances } from '@/hooks-v2/aggregator';
import { formatCadCents, parseCadToCents } from '@/utils/card-balance-input';
import type { CardBalanceFeedback, CardBalanceFormProps } from '@/types/card-balances.types';

function createOperationId(): string {
  return crypto.randomUUID();
}

export default function CardBalanceForm({
  merchantId,
  cardId,
  idPrefix,
}: CardBalanceFormProps): React.JSX.Element {
  const { t, language } = useTranslation();
  const locale = language === 'fr' ? 'fr-CA' : language === 'zh' ? 'zh-CA' : 'en-CA';
  const { balances, isError, isLoading, isSaving, save } = useCardBalances(merchantId, cardId);
  const [prepaid, setPrepaid] = useState('');
  const [bonus, setBonus] = useState('');
  const [feedback, setFeedback] = useState<CardBalanceFeedback>(null);
  // A retry of the same entry reuses its operation id; changed amounts get a new one.
  const operation = useRef<{ key: string; id: string } | null>(null);

  const submit = async (): Promise<void> => {
    if (isSaving || isLoading || isError) return;
    const prepaidCents = parseCadToCents(prepaid);
    const bonusCents = parseCadToCents(bonus);
    if (
      prepaidCents === null ||
      bonusCents === null ||
      (prepaidCents === undefined && bonusCents === undefined)
    ) {
      setFeedback('invalid');
      return;
    }
    const key = `${prepaidCents ?? ''}|${bonusCents ?? ''}`;
    if (operation.current === null || operation.current.key !== key) {
      operation.current = { key, id: createOperationId() };
    }
    setFeedback(null);
    try {
      await save({
        operation_id: operation.current.id,
        ...(prepaidCents !== undefined ? { prepaid_cents: prepaidCents } : {}),
        ...(bonusCents !== undefined ? { bonus_cents: bonusCents } : {}),
      });
      operation.current = null;
      setPrepaid('');
      setBonus('');
      setFeedback('saved');
    } catch (error: unknown) {
      const code = typeof error === 'object' && error !== null && 'code' in error ? error.code : null;
      setFeedback(code === 'CONFLICT' ? 'conflict' : 'failed');
    }
  };

  const latest = balances;
  const times = [latest?.prepaid?.received_at, latest?.bonus?.received_at].filter(
    (value): value is string => typeof value === 'string',
  );
  const lastUpdate = times.length > 0 ? times.sort().at(-1) : undefined;

  return (
    <form
      className="mt-4 space-y-3 border-t pt-4"
      aria-labelledby={`${idPrefix}-balances-heading`}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <h4 id={`${idPrefix}-balances-heading`} className="font-medium text-gray-900">
        {t('merchant.memberList.cardBalances.title')}
      </h4>
      {isError ? (
        <p role="alert">{t('merchant.memberList.cardBalances.loadFailed')}</p>
      ) : isLoading ? (
        <p role="status">{t('common.loading')}</p>
      ) : (
        <dl className="grid grid-cols-2 gap-2 text-sm text-gray-700">
          <dt>{t('merchant.memberList.cardBalances.currentPrepaid')}</dt>
          <dd>
            {latest?.prepaid ? formatCadCents(latest.prepaid.amount_cents, locale) : t('merchant.memberList.cardBalances.none')}
          </dd>
          <dt>{t('merchant.memberList.cardBalances.currentBonus')}</dt>
          <dd>
            {latest?.bonus ? formatCadCents(latest.bonus.amount_cents, locale) : t('merchant.memberList.cardBalances.none')}
          </dd>
          {lastUpdate !== undefined && (
            <>
              <dt>{t('merchant.memberList.cardBalances.lastUpdate')}</dt>
              <dd>{TimestampService.formatForDisplay(TimestampService.parseDateTimeInput(lastUpdate))}</dd>
            </>
          )}
        </dl>
      )}
      <div>
        <label htmlFor={`${idPrefix}-prepaid`} className="block text-sm font-medium">
          {t('merchant.memberList.cardBalances.prepaidLabel')}
        </label>
        <input
          id={`${idPrefix}-prepaid`}
          inputMode="decimal"
          value={prepaid}
          disabled={isSaving}
          placeholder={t('merchant.memberList.cardBalances.placeholder')}
          onChange={(event) => {
            setPrepaid(event.target.value);
            setFeedback(null);
          }}
          className="mt-1 w-full rounded border px-3 py-2"
        />
      </div>
      <div>
        <label htmlFor={`${idPrefix}-bonus`} className="block text-sm font-medium">
          {t('merchant.memberList.cardBalances.bonusLabel')}
        </label>
        <input
          id={`${idPrefix}-bonus`}
          inputMode="decimal"
          value={bonus}
          disabled={isSaving}
          placeholder={t('merchant.memberList.cardBalances.placeholder')}
          onChange={(event) => {
            setBonus(event.target.value);
            setFeedback(null);
          }}
          className="mt-1 w-full rounded border px-3 py-2"
        />
      </div>
      <p className="text-xs text-gray-500">{t('merchant.memberList.cardBalances.help')}</p>
      <button
        type="submit"
        disabled={isSaving || isLoading || isError}
        className="rounded bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50"
      >
        {isSaving ? t('common.saving') : t('merchant.memberList.cardBalances.save')}
      </button>
      {feedback === 'saved' && (
        <p role="status" className="text-green-700">
          {t('merchant.memberList.cardBalances.saved')}
        </p>
      )}
      {feedback !== null && feedback !== 'saved' && (
        <p role="alert" className="text-red-700">
          {t(`merchant.memberList.cardBalances.${feedback}`)}
        </p>
      )}
    </form>
  );
}
